/**
 * PoFinder — search the cached purchase orders across all WMS systems, pick one,
 * and (optionally) drill into its line items, without leaving the appointment
 * dialog or opening the WMS.
 *
 * Replaces the old single-PO "Validate" affordance. Searches dbo.OrdersCache
 * (Deposco POs + Camelot Receipts today; Extensiv when it syncs) via the dock
 * /appointments/po-search endpoint. The scheduler's current facility floats to
 * the top of results but everything is shown (a PO at another site is still
 * pickable). Selecting a PO calls onSelect with { poNumber, clientCode,
 * referenceNumber } so the parent dialog fills its fields.
 *
 * Detail drill-down is view-only.
 */
import { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { poSearch, poDetail, type PoSummary, type PoDetailResult } from '@/api/client';

interface PoFinderProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  facilityCode: string;                 // scheduler's current facility — used to sort, not filter
  /**
   * The direction being booked. Narrows the search to documents that direction
   * could use — inbound gets receipts and POs, outbound gets shipments and sales
   * orders. It MATTERS: Camelot document numbers are unique only per type, so
   * receipt 12605 and shipment 12605 both exist and are different trucks.
   * Omit while the form has no direction and both are searched.
   */
  direction?: 'INBOUND' | 'OUTBOUND';
  /**
   * ---- ⭐⭐ THE DATE BEING BOOKED, WHICH IS NOT TODAY (2026-09-22) ---------
   *
   * ⛔ The ±2-week window was first centred on TODAY, because this picker had no
   * idea what date the form was holding. Booking a truck six weeks out then
   * searched a fortnight around today -- nowhere near that load's paperwork --
   * and the scheduler would have seen an empty list and gone to the WMS.
   *   ** A WINDOW AROUND THE WRONG CENTRE IS NOT A NARROWER SEARCH, IT IS A
   *      SEARCH SOMEWHERE ELSE. **
   *
   * ⚠️ OPTIONAL, AND VALIDATED RATHER THAN TRUSTED. Both callers derive it from
   * a `datetime-local` string that is legitimately EMPTY on a fresh form, so an
   * unparseable value falls back to today instead of producing an Invalid Date.
   * ⭐ An exact match ignores the window entirely, so this never decides whether
   * a whole identifier is found -- only where a fuzzy search looks first.
   */
  anchorDate?: string;
  onSelect: (picked: {
    poNumber: string;
    clientCode?: string;
    referenceNumber?: string;
    /** The counterparty's code/reference on the picked document. On an outbound
     *  shipment these are the consignee's; the caller decides what to do with
     *  them, because inbound and outbound fill different fields. */
    customerCode?: string;
    customerReference?: string;
  }) => void;
  initialQuery?: string;                // if set (>=3 chars), open pre-filled + auto-search
}

/** Local calendar date as YYYY-MM-DD. Deliberately NOT toISOString(), which is
 *  UTC and hands a Denver scheduler tomorrow's date after 5pm. */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDate(d?: string | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString();
}
function fmtMoney(n?: number | null): string {
  if (n == null) return '';
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function PoFinder({ open, onClose, getToken, facilityCode, direction, onSelect, initialQuery, anchorDate }: PoFinderProps) {
  // The picker serves two jobs now. Nothing about the mechanics differs — only
  // the words, because a scheduler booking an outbound is not looking for a PO
  // and being told they are is how you make someone doubt the tool.
  const outbound = direction === 'OUTBOUND';

  /**
   * ---- ⭐⭐ THE ROW AWAITING AN "ARE YOU SURE" (DECISION 214) ---------------
   *
   * > Pete, 2026-09-22: *"If they try to add a SO to an inbound or PO to an
   * > outbound (or adjustment), warn them"* … *"Warn, then let them through."*
   *
   * ⛔ NOT `window.confirm`. A native dialog inside an open shadcn Dialog steals
   * focus, cannot be styled, and reads as a browser error rather than a decision
   * the app is asking for.
   * ⛔ AND NOT A NESTED <Dialog> EITHER — two stacked modals is how a scheduler
   * ends up unable to dismiss either.
   * ⭐ SO IT IS INLINE, IN THE ROW, where the badge that caused the warning is
   * still on screen next to the sentence explaining it.
   *   ** A CONFIRMATION SHOWN SOMEWHERE THE EVIDENCE IS NOT IS A CONFIRMATION
   *      ABOUT NOTHING. **
   *
   * ⚠️ CLEARED ON EVERY NEW SEARCH. A stale pending confirmation pointing at a
   * row that is no longer in the list is a button that does something invisible.
   */
  const [confirmRow, setConfirmRow] = useState<PoSummary | null>(null);

  const [query, setQuery] = useState('');
  /**
   * YYYY-MM-DD, or '' for none. ANDed with the text; either alone will search.
   *
   * DEFAULTS TO TODAY, and STAYS APPLIED while you type. Pete, 2026-08-14.
   * The trade-off was made deliberately: a text search is now scoped to the
   * date in the box, so "PWN" finds PWN receipts DUE TODAY, not every PWN
   * receipt. That is the common case -- a scheduler is nearly always working
   * today's board -- but it means a search can come back empty for a document
   * that plainly exists, just on another day. Two things carry the weight of
   * that: the date is always VISIBLE in the box (never an invisible filter),
   * and the empty-result message names the date and offers to drop it.
   */
  /**
   * ---- ⭐⭐ THE DEFAULT IS A FORTNIGHT, NOT A DAY (2026-09-22) -------------
   *
   * ⛔⛤ It was ~~useState(todayIso)~~ -- a single day. The reasoning above was
   * sound and is struck only in its NARROWNESS: a scheduler is usually working
   * today's board, but "usually" sent them to the WMS for anything that slipped
   * a day.
   *
   * ⭐ Pete: *"How about we search dates 2 weeks back and 2 weeks forward to
   * narrow the results. Stating somewhere that is the default so they can expand
   * if necessary."*
   *
   * ⚠️ THREE STATES NOW, AND ONLY ONE CONTROL CHANGED. `onDate` set = that single
   * day (unchanged). `onDate` empty and not widened = the ±2-week range. Widened
   * = no date filter at all. ⛔ AND AN EXACT MATCH IGNORES ALL THREE, decided on
   * the SERVER -- this screen cannot narrow a whole identifier even by accident.
   */
  const [onDate, setOnDate] = useState('');
  /**
   * ⚠️ `true` = every date. Not the same as `onDate === ''`.
   *
   * ---- ⛔⛤ DEFAULTS TO TRUE SINCE 2026-09-24 ------------------------------
   *
   * > Pete: *"the Any Date button is still there. have it search all at first
   * > because I can't get you to get this right. Have the date range as an
   * > option."*
   *
   * ⛔ IT DEFAULTED TO THE +/-2-WEEK WINDOW, and that window is invisible until
   * it has already cost you the answer. A scheduler types a number he is holding
   * in his hand, gets "Nothing found", and has no reason to suspect a date
   * filter he never set.
   *   ** A DEFAULT THAT NARROWS IS A FILTER NOBODY CHOSE, AND ITS FALSE
   *      NEGATIVES ARE INDISTINGUISHABLE FROM AN EMPTY DATABASE. **
   *
   * ⚠️ THE WINDOW ITSELF IS NOT DELETED — Pete asked for the range as an OPTION,
   * and it is still the right tool once a number turns out to be common. What
   * changed is which way round the default sits: find it first, narrow second.
   *   ** NARROWING IS CHEAP TO ASK FOR AND EXPENSIVE TO UNDO, BECAUSE UNDOING IT
   *      REQUIRES KNOWING IT HAPPENED. **
   */
  const [wideOpen, setWideOpen] = useState(true);
  /** ⭐ What the SERVER says it searched, so the line under the box is not a guess. */
  const [dateScope, setDateScope] = useState<'exact' | 'range' | 'day' | 'none' | null>(null);

  /* ⛔ THE RANGE IS DERIVED, NEVER STORED. Two dates in state would drift from
     `todayIso` the moment the dialog stayed open past midnight -- and a picker
     open across a shift change is the normal case on a dock.
       ** A WINDOW AROUND "TODAY" MUST BE COMPUTED WHEN IT IS USED, NOT WHEN THE
          COMPONENT MOUNTED. ** */
  /**
   * ⛔ THE CENTRE, VALIDATED. `startLocal.slice(0, 10)` is '' on a fresh form and
   * could in principle be anything; `new Date('T12:00:00Z')` is an Invalid Date
   * and every arithmetic below it silently becomes NaN.
   *   ** A DATE TAKEN FROM A FORM FIELD IS A STRING UNTIL IT HAS BEEN CHECKED. **
   */
  const windowCentre = () =>
    (anchorDate && /^\d{4}-\d{2}-\d{2}$/.test(anchorDate)) ? anchorDate : todayIso();

  const rangeOf = (isoToday: string, days = 14) => {
    const d = new Date(`${isoToday}T12:00:00Z`);
    const shift = (n: number) => {
      const x = new Date(d);
      x.setUTCDate(x.getUTCDate() + n);
      return x.toISOString().slice(0, 10);
    };
    return { from: shift(-days), to: shift(days) };
  };
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Guidance, not failure. Rendered muted — a red message for "type a bit more"
   *  reads as something having gone wrong, and this is just the next step. */
  const [hint, setHint] = useState<string | null>(null);
  const queryRef = useRef<HTMLInputElement>(null);
  const [results, setResults] = useState<PoSummary[] | null>(null);

  // detail drill-down
  const [detailFor, setDetailFor] = useState<PoSummary | null>(null);
  const [detail, setDetail] = useState<PoDetailResult | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  /**
   * EVERY open starts on today's date -- with or without a seeded number.
   * Pete, 2026-08-14: the box showing a blank mm/dd/yyyy was the complaint.
   *
   * The earlier build seeded today ONLY when the box was empty, so opening the
   * picker from a form that already had a number showed a blank date. Two
   * behaviours from one control is the thing worth removing here: now the date
   * is always set, always visible, and always part of the search.
   */
  useEffect(() => {
    if (!open) return;
    const seed = (initialQuery ?? '').trim();
    const iso = todayIso();
    setQuery(seed);
    setOnDate(iso);
    setError(null);
    setHint(null);
    setResults(null);
    setDetailFor(null);
    setDetail(null);
    setDetailError(null);
    void runSearchFor(seed, iso);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const runSearchFor = async (raw: string, date = onDate, wide = wideOpen) => {
    const q = raw.trim();
    // A date is a complete search on its own — "what is due Friday" has no number
    // in it. Only refuse when there is neither.
    if (q.length < 3 && !date) {
      // Not an error — this is the all-dates state waiting for a number. Say what
      // to do next rather than what went wrong.
      setHint('Searching every date — type at least 3 characters.');
      setError(null);
      setResults(null);
      queryRef.current?.focus();
      return;
    }
    setSearching(true);
    setError(null);
    setHint(null);
    setResults(null);
    setDetailFor(null);
    setDetail(null);
    try {
      const token = await getToken();
      /* ⛔ THE RANGE IS SENT ONLY WHEN NEITHER A SINGLE DATE NOR "every date" is
         chosen. Sending it alongside a single date would make the server pick,
         and a screen that sends two conflicting filters has stopped knowing what
         it asked for. */
      const useRange = !date && !wide;
      /* ⛔⛤ THIS READ `rangeOf(todayIso)` -- THE FUNCTION, NOT ITS RESULT.
         `new Date("function todayIso()...T12:00:00Z")` is an Invalid Date and
         `.toISOString()` throws on it, so every ranged search would have died at
         runtime. tsc would have caught it (TS2345); my per-file check filters to
         TS1xxx syntax errors and cannot see an argument-type mismatch.
           ** A CHECK THAT ONLY READS SYNTAX CANNOT TELL A FUNCTION FROM ITS
              RETURN VALUE. ** */
      const r = useRange ? rangeOf(windowCentre()) : null;
      const out = await poSearch(
        token, q, facilityCode, direction, date || undefined, r?.from, r?.to,
      );
      const rows = out.results;
      setResults(rows);
      /* ⚠️ See the note on `confirmRow`: a pending confirmation that outlives the
         list it pointed into is a live button aimed at a row nobody can see. */
      setConfirmRow(null);
      /* ⚠️ `?? null`, NOT a default of 'range'. An older backend sends nothing,
         and the line under the box must then say nothing rather than assert a
         window that was never applied. */
      setDateScope(out.dateScope ?? null);
      if (rows.length === 0) {
          // Say what was actually searched. "No POs found" while silently
          // excluding every shipment — or every date but one — is the kind of
          // message that sends someone to the WMS.
          /* ⛔⛤ ~~const scope = ' in receipts / purchase orders'~~ — struck with
             DECISION 214 (§0.3). It described the order-type filter, which no
             longer exists: this search now looks at every document type, exactly
             like the calendar box.
             ⚠️ IT WAS THE MOST CONVINCING SENTENCE IN THE OLD MESSAGE and the
             most misleading. "Nothing found starting with 3275284-00 in receipts
             / purchase orders" reads as a narrow, well-understood miss. It was
             actually a document sitting in the cache with a type nobody had told
             the scheduler about.
               ** A SCOPE SENTENCE THAT OUTLIVES ITS SCOPE IS WORSE THAN NO
                  SENTENCE: IT SPENDS THE CREDIBILITY THE HONEST ONE EARNED. ** */
          const scope = '';
          const what = q.length >= 3 ? `starting with "${q}"` : '';
          const when = date ? `${q.length >= 3 ? ' ' : ''}dated ${date}` : '';
          // THE DATE IS THE LIKELY CULPRIT, so say so in the same breath. With
          // the date always applied, "nothing found" for a document that exists
          // on another day is the expected miss, not an edge case -- and a
          // scheduler who cannot see why will go to the WMS instead.
          /**
           * ---- ⛔⛤ THE DATE ADVICE IS SUPPRESSED WHEN THE DATE IS NOT THE
           *          PROBLEM (Pete, 2026-09-22) ---------------------------------
           *
           * > Pete: *"ANY doesn't work either"*.
           *
           * ⛔⛔ IT DID WORK. "Any date" cleared the date and searched every one of
           * them. The document was still not returned because it is an OUTBOUND
           * document and the appointment is inbound. ⚠️ But the message offered the
           * date remedy anyway, he took it, it changed nothing, and from where he
           * sat the honest conclusion was that the control is broken.
           *
           *   ** A REFUSAL THAT OFFERS A REMEDY THAT CANNOT WORK TEACHES THE USER
           *      THAT THE CONTROL IS BROKEN. **
           *
           * ⭐ `!other` IS THE WHOLE CHANGE. When the row exists in the other
           * direction no date will ever surface it, and a sentence about dates is
           * not a smaller mistake for being true.
           * ⚠️ DECLARED AFTER `other`, not before. A const read above its own
           * declaration throws, and this file has no compiler watching it at
           * runtime.
           */
          /**
           * ---- ⛔⛔ IT EXISTS — AS THE OTHER DIRECTION (#363) ---------------
           *
           * ⚠️ THE MESSAGE THIS REPLACES WAS TRUE AND USELESS: *"Nothing found
           * starting with 184244 in receipts / purchase orders."* Correct to the
           * letter, and read as "this document does not exist" — while the
           * number sits in the cache as a SHIPMENT.
           *
           * ⛔ THE TYPE FILTER IS RIGHT AND STAYS. Camelot numbers are unique
           * only per type, so handing back both would offer the wrong truck. The
           * fix is to stop CLAIMING ABSENCE about something that is present.
           *
           * ⭐ AND IT NAMES THE WAY OUT. §1Z.2 — a dead end that says which one
           * click resolves it is not a dead end.
           */
          /* ⛔⛤ ~~const other = out.otherDirection~~ and its sentence — struck.
             The server stopped computing it (DECISION 214) because there is no
             longer an absence for it to explain: a document in the other
             direction now appears in the list with a badge on it.
             ⚠️ AN EMPTY RESULT NOW MEANS WHAT IT SAYS. That is new, and it is the
             first time this box has been able to claim it. */
          /* ---- ⛔⛔⛤ NAME THE TABLE. THIS IS THE WHOLE FIX (2026-09-24) -------
           *
           * > Pete: *"the search is still not the same across all the searchs
           * > WHY!!!!!! ... It works on the general search bar but not in the
           * > edit screen."*
           *
           * ⛔ THE SEARCHES WERE NEVER DISAGREEING. They share the matching code
           * (`buildOrderSearchStems` + `buildOrderMatchClause`) and always have.
           * They look in DIFFERENT TABLES: the calendar's box searches
           * `dock.Appointment` AND the WMS; this box and the orders rail search
           * `dbo.OrdersCache` only.
           *
           * ⚠️ SO "Nothing found starting with 6035952011" WAS TRUE AND USELESS.
           * That number is an APPOINTMENT reference (A00000422, AVIENT, arrived,
           * door 46). It is not in Camelot or Deposco and never was. The sentence
           * reported an absence without saying absence FROM WHAT, so it read as
           * "this number does not exist".
           *   ** AN ABSENCE IS ONLY INFORMATION IF YOU SAY WHAT IT IS ABSENT
           *      FROM. OTHERWISE IT IS READ AS ABSENT FROM EVERYTHING. **
           *
           * ⭐ AND IT NAMES THE WAY OUT (S1Z.2). A dead end that says which one
           * click resolves it is not a dead end.
           */
          const hint = date && q.length >= 3
            ? ' Searching that date only — clear the date to search them all.'
            : '';
          const elsewhere = q.length >= 3
            ? ' This searches Camelot and Deposco only. If it is an appointment'
              + ' reference, use the search box above the calendar — that one'
              + ' looks at bookings too.'
            : '';
          const absence = `Nothing found ${what}${when}${scope}.`.replace(/\s+/g, ' ');
          setError(absence + hint + elsewhere);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  const runSearch = () => runSearchFor(query, onDate, wideOpen);

  const openDetail = async (row: PoSummary) => {
    setDetailFor(row);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const token = await getToken();
      /* ⛔ #342 - facilityCode is what scopes this to your company now. */
      const d = await poDetail(token, row.poNumber, row.wmsSource, row.orderType, facilityCode);
      setDetail(d);
    } catch (err) {
      setDetail(null);
      setDetailError(err instanceof Error ? err.message : 'Could not load line items.');
    } finally {
      setDetailLoading(false);
    }
  };

  const choose = (row: PoSummary, confirmed = false) => {
    /**
     * ⛔⛔ `=== true`, NOT TRUTHINESS. `directionMismatch` is null when no
     * direction was sent, and `if (row.directionMismatch)` would then be false —
     * which is the right answer for the wrong reason, and stops being the right
     * answer the first time the field becomes optional on an older server.
     * ⚠️ AN OLDER SERVER SENDS NEITHER FIELD, so this is `undefined` there and
     * the picker behaves exactly as it did yesterday: no badge, no warning, no
     * crash. That is deliberate — the frontend may be live for minutes before
     * the backend is.
     *   ** A SCREEN THAT REQUIRES A FIELD THE SERVER MAY NOT SEND HAS COUPLED
     *      TWO DEPLOYS THAT GO OUT SEPARATELY. **
     */
    if (row.directionMismatch === true && !confirmed) {
      setConfirmRow(row);
      return;
    }
    onSelect({
      poNumber: row.poNumber,
      clientCode: row.clientCode ?? undefined,
      referenceNumber: undefined, // header has no separate reference today; left for scheduler
      customerCode: row.customerCode ?? undefined,
      customerReference: row.customerReference ?? undefined,
    });
    onClose();
  };

  const reset = () => {
    setQuery(''); setResults(null); setError(null); setDetailFor(null); setDetail(null); setDetailError(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{outbound ? 'Find a shipment' : 'Find a PO'}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground -mt-1 mb-2">
          {outbound
            ? 'Search shipments and sales orders across all warehouse systems. Type at least 3 characters of the shipment, order or reference number \u2014 or a client name to browse theirs.'
            : 'Search purchase orders across all warehouse systems. Type at least 3 characters of the PO number, a reference \u2014 or a client name to browse theirs.'}
          {' '}Results at your current facility ({facilityCode || '—'}) are listed first.
        </p>

        {/* search bar */}
        <div className="flex gap-2">
          <Input
            autoFocus
            /* CONTAINER AND BOL ARE DELIBERATELY ABSENT FROM THIS PLACEHOLDER,
               even though poSearch matches both columns. Probed the live Camelot
               API 2026-08-14: Container is empty on 0/247 open and 0/500 shipped
               rows, BOL_Document on 0/500 (and BOL_Print_date is 0001-01-01 --
               NAV's blank -- on every row, so nothing is recording a BOL under
               another name either). Symbia's container numbers come off the
               carrier, the customer or the driver's paperwork, never the WMS.
               Advertising a number the picker can never return is how a scheduler
               learns not to trust it. RESTORE BOTH WORDS when driver check-in
               starts writing dock.Appointment.ContainerNumber -- the columns and
               the matchClause are already in place and waiting. */
            placeholder={outbound
              ? 'Shipment #, PRO, reference or client'
              : 'PO #, receipt #, PRO or client'}
            ref={queryRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
          />
          <input
            type="date"
            aria-label="Document date"
            value={onDate}
            /* ⚠️ Picking a day also LEAVES the widened state -- otherwise the
               scheduler picks a date, sees every date searched, and has no way
               to tell which control won. */
            onChange={(e) => { setOnDate(e.target.value); if (e.target.value) setWideOpen(false); }}
            className="shrink-0 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
          {/* A TWO-STATE TOGGLE, never a dead control.
              The date narrows every search now, so getting out of it has to be
              one click from wherever you are. The first cut DISABLED this button
              until 3 characters were typed -- which meant that from a freshly
              opened picker, the state you most want (all dates) was unreachable
              by clicking the button labelled "Any date". A visible control that
              does nothing when pressed teaches people to stop pressing it.
              Now it always acts: it clears the date, and if there is nothing to
              search yet it focuses the box and says what to type. The label
              flips to "Today" once cleared, so the trip back is also one click.
              (Clearing a native date input otherwise means hunting its built-in
              X, which differs per browser and is invisible under pressure.) */}
          {onDate ? (
            <Button
              type="button"
              variant="outline"
              className="shrink-0"
              disabled={searching}
              onClick={() => {
                setOnDate('');
                /* ⛔ AND THE WINDOW TOO. A button labelled "Any date" that left a
                   fortnight filter running would be the invisible filter the
                   whole design of this box exists to avoid. */
                setWideOpen(true);
                if (query.trim().length >= 3) void runSearchFor(query, '', true);
                else {
                  setHint('Searching every date — type at least 3 characters.');
                  setError(null);
                  setResults(null);
                  queryRef.current?.focus();
                }
              }}
              title="Search every date"
            >
              Any date
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="shrink-0"
              disabled={searching}
              onClick={() => { const iso = todayIso(); setOnDate(iso); setWideOpen(false); void runSearchFor(query, iso, false); }}
              title="Back to today only"
            >
              Today
            </Button>
          )}
          {/**
            * ---- ⭐ MOTION, NOT A CHANGED LABEL (Pete, 2026-09-22) -------------
            *
            * > Pete: *"There is no progress bar so you don't know its searching."*
            *
            * ⚠️ THE LABEL ALREADY CHANGED to "Searching…" and he did not see it.
            * A word that swaps once, on a small button at the right edge, is
            * indistinguishable from a button that has always said that.
            *   ** A LABEL THAT CHANGES ONCE LOOKS LIKE A LABEL. ONLY MOTION READS
            *      AS PROGRESS. **
            * ⭐ THE SPINNER IS THE ONE THE CALENDAR SEARCH ALREADY USES, so the two
            * boxes say "working" the same way rather than each inventing it.
            * ⛔ THE TEXT STAYS. Pete: *"FIND PO is probably ok"* - this dialog has
            * room for a worded button and the icon-only treatment is for the two
            * cramped rails.
            */}
          <Button onClick={runSearch} disabled={searching || (query.trim().length < 3 && !onDate)}>
            {searching && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {searching ? 'Searching…' : 'Search'}
          </Button>
        </div>

        {/**
          * ---- ⭐⭐ SAY WHAT WAS SEARCHED, EVERY TIME ------------------------
          *
          * ⛔ Pete: *"Stating somewhere that is the default so they can expand if
          * necessary."*
          *   ** AN EMPTY LIST IS ONLY HONEST IF IT SAYS WHERE IT LOOKED. **
          *
          * ⚠️ IT READS THE SERVER'S ANSWER, not this component's intent. The two
          * differ in the case that matters most: a whole identifier makes the
          * server drop the date filter, and a line that reported the fortnight
          * anyway would be describing a search that did not run.
          *   ** A SCOPE SENTENCE BUILT FROM WHAT WAS ASKED, RATHER THAN WHAT
          *      HAPPENED, IS A GUESS WITH GOOD POSTURE. **
          * ⭐ 'exact' is the interesting one and it is phrased as a reassurance:
          * the scheduler typed a whole number and does not need to widen.
          */}
        {dateScope && !error && (
          <div className="mt-2 text-xs text-muted-foreground">
            {dateScope === 'exact'
              ? 'Exact match — searched every date and every facility.'
              : dateScope === 'range'
                /* ⛔⛤ ~~"the default"~~ — IT IS NOT THE DEFAULT ANY MORE (2026-09-24).
                   Every date is. The window is now something a scheduler asks for,
                   so this line describes a CHOICE rather than announcing a filter
                   he did not make. Saying "the default" here after the change
                   would be the same class of untrue-but-plausible sentence as the
                   kiosk's "Pill colors match the dock schedule". */
                ? `Searched 2 weeks either side of ${windowCentre()}. Clear the date to search every date.`
                : dateScope === 'day'
                  ? 'Searched that date only.'
                  : 'Searched every date.'}
          </div>
        )}

        {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
        {hint && !error && <div className="text-sm text-muted-foreground mt-2">{hint}</div>}

        {/* results */}
        {results && results.length > 0 && (
          <>
          {/* THE CAP HAS TO SAY SO. The API returns TOP 50, facility-ranked then
              newest — right for a number search, quietly wrong for a client
              browse: EBOTTLES has thousands of documents and the one they want
              may not be in the newest 50. Silently showing a slice of a big set
              is how someone concludes the record isn't there. */}
          {results.length >= 50 && (
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Showing the 50 most recent matches. Add more characters — a PO,
              reference or shipment number — to narrow it down.
            </div>
          )}
          <div className="mt-3 max-h-80 overflow-y-auto border rounded-md divide-y">
            {results.map((r) => (
              <div
                key={`${r.wmsSource}:${r.poNumber}`}
                className={`p-3 flex items-start justify-between gap-3 ${r.isCurrentFacility ? 'bg-amber-50' : ''}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{r.poNumber}</span>
                    <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                      {r.wmsSource}
                    </span>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${r.isCurrentFacility ? 'bg-amber-200 text-amber-900' : 'bg-slate-100 text-slate-600'}`}>
                      {r.facilityCode ?? '—'}
                    </span>
                    {/**
                      * ---- ⭐⭐ THE BADGE THAT REPLACED A DISAPPEARANCE ---------
                      *
                      * ⛔ BEFORE DECISION 214 THIS ROW WAS NOT HERE AT ALL. The
                      * server filtered it out and the picker said "nothing found".
                      * The badge is the entire compensation for widening the
                      * search, so it has to be legible at a glance, next to the
                      * number, in the same line the scheduler is already reading.
                      *
                      * ⚠️ TWO DIFFERENT FACTS, ONE BADGE SLOT. A Sales Order on an
                      * inbound is "this runs the other way" — recoverable, often
                      * intended. An Adjustment is "this app has no rule for this
                      * document type" — a different thing, and saying OUTBOUND
                      * about it would be inventing a direction the type does not
                      * have (see directionOfOrderType: null is never a default).
                      */}
                    {r.directionMismatch === true && (
                      <span
                        className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300"
                        title={r.orderDirection
                          ? `This is an ${r.orderDirection === 'INBOUND' ? 'inbound' : 'outbound'} document (${r.orderType}) and you are booking an ${outbound ? 'outbound' : 'inbound'} appointment.`
                          : `${r.orderType} is not a document this app maps to a direction, so it cannot be checked against the appointment.`}
                      >
                        {r.orderDirection ?? r.orderType}
                      </span>
                    )}
                  </div>
                  {/* The number the searcher probably typed. A Camelot receipt is
                      listed by its receipt number, so showing the PO # is what
                      explains why this row matched. */}
                  {r.customerOrderNumber && r.customerOrderNumber !== r.poNumber && (
                    <div className="text-xs mt-0.5 truncate">
                      <span className="text-muted-foreground">PO </span>
                      <span className="font-medium">{r.customerOrderNumber}</span>
                    </div>
                  )}
                  {/* Same job for the OTHER two numbers a receipt carries. A row
                      that came back because the scheduler typed a Rcv From
                      reference is indistinguishable from a wrong result unless
                      the number that matched is on screen. */}
                  {/* Include the counterparty NAME, now that it is searchable:
                      a row that came back because someone typed "Crystal" has to
                      show Crystal somewhere or it reads as a wrong result. */}
                  {(r.customerReference || r.customerCode || r.customer) && (
                    <div className="text-xs mt-0.5 truncate">
                      {/* DIRECTION-DEPENDENT LABEL, for the same reason the columns
                          are called Customer* and not ReceiveFrom*: Camelot has ONE
                          field pair whose meaning flips with document type. On a
                          shipment these are the CONSIGNEE's, and calling them
                          "Rcv from" on an outbound result is simply wrong -- which
                          is what shipped on the first cut (caught 2026-08-14). */}
                      <span className="text-muted-foreground">{outbound ? 'Ship to ' : 'Rcv from '}</span>
                      {[r.customerCode, r.customer, r.customerReference]
                        .filter(Boolean)
                        .map((v, i, arr) => (
                          <span key={i}>
                            <span className="font-medium">{v}</span>
                            {i < arr.length - 1 && <span className="text-muted-foreground"> · </span>}
                          </span>
                        ))}
                    </div>
                  )}
                  {/* Carrier-side numbers (migration 030). Same rule as every
                      other line here: if it is searchable it has to be visible,
                      or a match on it reads as a wrong result. */}
                  {(r.containerNumber || r.bolNumber || r.proNumber) && (
                    <div className="text-xs mt-0.5 truncate">
                      {r.containerNumber && (
                        <><span className="text-muted-foreground">Container </span>
                          <span className="font-medium">{r.containerNumber}</span></>
                      )}
                      {r.containerNumber && (r.bolNumber || r.proNumber) && <span className="text-muted-foreground"> · </span>}
                      {r.bolNumber && (
                        <><span className="text-muted-foreground">BOL </span>
                          <span className="font-medium">{r.bolNumber}</span></>
                      )}
                      {r.bolNumber && r.proNumber && <span className="text-muted-foreground"> · </span>}
                      {r.proNumber && (
                        <><span className="text-muted-foreground">PRO </span>
                          <span className="font-medium">{r.proNumber}</span></>
                      )}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground mt-1 truncate">
                    {r.clientCode && <>Client {r.clientCode} · </>}
                    {r.supplierName && <>{r.supplierName} · </>}
                    {fmtDate(r.placedDate || r.createdDate)}
                    {r.orderTotal != null && <> · {fmtMoney(r.orderTotal)}</>}
                    {r.status && <> · {r.status}</>}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {confirmRow && confirmRow.wmsSource === r.wmsSource && confirmRow.poNumber === r.poNumber ? (
                    /* ⭐ THE WARNING SAYS WHAT IS WRONG AND LETS THEM PAST IT.
                       ⛔ NOT "Are you sure?" — a question with no content is
                       answered Yes by everyone, every time, and teaches people to
                       click through the next one too.
                         ** A CONFIRMATION THAT DOES NOT NAME THE MISMATCH IS A
                            SPEED BUMP, NOT A CHECK. ** */
                    <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded p-1.5 max-w-[15rem]">
                      <div className="font-medium">
                        {r.orderDirection
                          ? `That is an ${r.orderDirection === 'INBOUND' ? 'inbound' : 'outbound'} document.`
                          : `${r.orderType} is not a truck document.`}
                      </div>
                      <div className="mt-0.5">
                        {r.orderDirection
                          ? `You are booking an ${outbound ? 'outbound' : 'inbound'} appointment.`
                          : 'This app has no direction rule for that type.'}
                      </div>
                      <div className="mt-1 flex gap-1">
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]"
                                onClick={() => choose(r, true)}>Use it anyway</Button>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]"
                                onClick={() => setConfirmRow(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => choose(r)}>{outbound ? 'Use this shipment' : 'Use this PO'}</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openDetail(r)}>View detail</Button>
                </div>
              </div>
            ))}
          </div>
          </>
        )}

        {/* detail panel (view-only) */}
        {detailFor && (
          <div className="mt-3 border rounded-md p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-sm">
                {detailFor.poNumber} <span className="text-muted-foreground font-normal">· {detailFor.wmsSource} · {detailFor.facilityCode ?? '—'}</span>
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setDetailFor(null); setDetail(null); setDetailError(null); }}>Close detail</Button>
            </div>
            {detailLoading && <div className="text-sm text-muted-foreground">Loading line items…</div>}
            {!detailLoading && detailError && (
              <div className="text-sm text-red-600">Couldn't load line items: {detailError}</div>
            )}
            {/* ⭐ F80: header read, lines timed out. ⛔ Not "no line items". */}
            {!detailLoading && !detailError && detail && detail.linesError === 'timeout' && (
              <div className="flex items-center gap-2 text-sm text-amber-700">
                <span>The line items took too long to load — the database was busy.</span>
                <Button size="sm" variant="outline" onClick={() => void openDetail(detailFor)}>Retry</Button>
              </div>
            )}
            {!detailLoading && !detailError && detail && detail.linesError !== 'timeout' && detail.lines.length === 0 && (
              <div className="text-sm text-muted-foreground">No line items available for this PO.</div>
            )}
            {!detailLoading && !detailError && detail && detail.lines.length > 0 && (
              <div className="max-h-56 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-muted-foreground">
                    <tr>
                      <th className="py-1 pr-2">#</th>
                      <th className="py-1 pr-2">Item</th>
                      <th className="py-1 pr-2 text-right">Ordered</th>
                      {/* ⛔⛤ ~~Recv'd~~ 2026-09-27 — it renders `ShippedQuantity`, and
                          `OrderLinesCache` has no received column at all. */}
                      <th className="py-1 pr-2 text-right">Shipped</th>
                      <th className="py-1 pr-2">UOM</th>
                      <th className="py-1 pr-2 text-right">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.lines.map((l, i) => (
                      <tr key={i} className="border-t">
                        <td className="py-1 pr-2">{l.lineNumber ?? i + 1}</td>
                        <td className="py-1 pr-2">{l.itemNumber ?? '—'}</td>
                        <td className="py-1 pr-2 text-right">{l.orderedQty ?? '—'}</td>
                        <td className="py-1 pr-2 text-right">{l.shippedQty ?? '—'}</td>
                        <td className="py-1 pr-2">{l.uom ?? '—'}</td>
                        <td className="py-1 pr-2 text-right">{l.unitPrice != null ? fmtMoney(l.unitPrice) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-2">
              <Button size="sm" onClick={() => choose(detailFor)}>{outbound ? 'Use this shipment' : 'Use this PO'}</Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
